// Orbital Swap · Phase 2a — BCH → MBA claim end-to-end via WizardConnect
//
// Real chipnet transaction path. Builds a DeviantOpenMintFT.cash claim() tx
// with cashscript (using a throwaway signer to satisfy TransactionBuilder's
// SignatureTemplate contract; the produced sigs are placeholders), packages
// the raw hex + sourceOutputs into a WcSignTransactionRequest, hands it to
// the paired wallet via WizardConnect. The wallet identifies its own inputs
// by matching sourceOutputs.lockingBytecode against its addresses, RE-SIGNS
// with its actual key (discarding the throwaway sigs), and either broadcasts
// itself (broadcast: true) or returns the signed hex for us to broadcast.
//
// Reference: scripts/chipnet-asm-claim.mjs — same output shape, same covenant
// param order. Only difference is signer: script uses a WIF loaded from disk;
// mockup uses WC round-trip.

import artifact from './artifacts/DeviantOpenMintFT.json';
import shareArtifact from './artifacts/DeviantShareMBA.json';
import type { Artifact } from 'cashscript';
import { Contract, ElectrumNetworkProvider, TransactionBuilder, SignatureTemplate } from 'cashscript';
import { secp256k1, decodeCashAddress } from '@bitauth/libauth';
import { ElectrumClient, type AddressUtxo } from './electrum';
import type { WcClient } from './wc';
import {
  appendPlatformFeeOutputs, computePlatformFee, PLATFORM_FEE_V1, PLATFORM_ADDRESSES,
  type PlatformFeeMath,
} from './platformFee';

const ART = artifact as unknown as Artifact;
const SHARE_ART = shareArtifact as unknown as Artifact;

/** Chipnet MBA registry — the known claim-target categories the mockup can
 *  currently build tx for. Kept in sync with the seed in
 *  shells/app/src/core/wallet.ts CHIPNET_DEMO_TOKENS. */
export interface KnownMba {
  ticker: string;
  categoryId: string;
  rateSats: bigint;
  founderFeeBps: bigint;
  founderLockingBytecodeHex: string;
  contractAddress: string;
  contractTokenAddress: string;
  /** DVMB templateId — undefined = ArtemisionMintFT/DeviantOpenMintFT (the
   *  original static-rate open-mint shape). 0x01 = DeviantShareMBA (uses
   *  claimViaLocalSignerShare below). 0x02 = DeviantOracleMBA (claim path
   *  still to land). */
  templateId?: number;
  /** DeviantShareMBA-specific fields — only present when templateId === 0x01.
   *  companyLockingBytecodeHex reuses founderLockingBytecodeHex above (Share
   *  publishes stash the company treasury there); distribution + splitBps
   *  need dedicated slots to reconstruct the covenant. */
  distributionLockingBytecodeHex?: string;
  companySplitBps?: bigint;
}

// Static seed — the two long-lived chipnet MBAs the mockup ships with.
// Locally-created assets are merged in at boot via mergeKnownMbas() (see
// genesis.ts loadLocalKnownMbas → main.ts). The map is `let` rather than
// `const Readonly` because that merge mutates it in place.
export const KNOWN_MBAS: Record<string, KnownMba> = {
  ASM: {
    ticker: 'ASM',
    categoryId: '09af8a5b2fc2c9a2449045a8d5a6aa60db493989682eca257a57fa8d75d67231',
    rateSats: 100n,
    founderFeeBps: 100n,
    founderLockingBytecodeHex: '76a9149aa6baabb2bbb1d1940e4ec82b9640a7ab62b8dc88ac',
    contractAddress: 'bchtest:pw8ypezzy6gv87tq96ey7p60vx9rgl4v5cq4lseg5luk9ktmn20ysd5k97aey',
    contractTokenAddress: 'bchtest:rw8ypezzy6gv87tq96ey7p60vx9rgl4v5cq4lseg5luk9ktmn20ysl82y8uq0',
  },
  ASM2: {
    ticker: 'ASM2',
    categoryId: '8a9f592d4a3bdbc1bc23e57c151434cbebb79bc3a00a07e7f457c9ae8fea2815',
    rateSats: 1000n,
    founderFeeBps: 100n,
    founderLockingBytecodeHex: '76a9149aa6baabb2bbb1d1940e4ec82b9640a7ab62b8dc88ac',
    contractAddress: 'bchtest:pdkzq8dq34774s2l7xpw8d8cjjr6sthcfmrknglpe5a76f0vj63ycuqljv9c0',
    contractTokenAddress: 'bchtest:rdkzq8dq34774s2l7xpw8d8cjjr6sthcfmrknglpe5a76f0vj63ycwnrn4ypy',
  },
};

/** Merge extra MBAs into KNOWN_MBAS. Later entries win on ticker collision;
 *  ticker collisions are logged so a locally-created "ASM" doesn't quietly
 *  replace the chipnet-canonical one. Called at boot with the local cache. */
export function mergeKnownMbas(extras: Readonly<Record<string, KnownMba>>): void {
  for (const [ticker, m] of Object.entries(extras)) {
    if (KNOWN_MBAS[ticker] && KNOWN_MBAS[ticker].categoryId !== m.categoryId) {
      console.warn('[orbital] ticker collision — replacing', ticker,
        'seed', KNOWN_MBAS[ticker].categoryId.slice(0, 8), '→ local', m.categoryId.slice(0, 8));
    }
    KNOWN_MBAS[ticker] = m;
  }
}

const DUST = 1000n;
const MINER_FEE = 3000n;

function hexToBin(h: string): Uint8Array {
  const c = h.startsWith('0x') ? h.slice(2) : h;
  const out = new Uint8Array(c.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(c.slice(i * 2, i * 2 + 2), 16);
  return out;
}
function txidLE(hex: string): Uint8Array {
  const d = hexToBin(hex);
  const o = new Uint8Array(d.length);
  for (let i = 0; i < d.length; i++) o[i] = d[d.length - 1 - i];
  return o;
}
function p2pkhLB(pkh: Uint8Array): Uint8Array {
  const o = new Uint8Array(25);
  o[0] = 0x76; o[1] = 0xa9; o[2] = 0x14; o.set(pkh, 3); o[23] = 0x88; o[24] = 0xac;
  return o;
}
function randomPrivateKey(): Uint8Array {
  const k = new Uint8Array(32);
  crypto.getRandomValues(k);
  // ensure it's a valid secp256k1 scalar (in [1, n-1]); if not, retry
  const valid = secp256k1.derivePublicKeyCompressed(k);
  if (typeof valid === 'string') return randomPrivateKey();
  return k;
}

// ── preview (no tx build, just the numbers the review sheet needs) ─────────

export interface ClaimTxPreview {
  ticker: string;
  amountUnits: bigint;
  grossSats: bigint;
  feeSats: bigint;
  minerFee: bigint;
  totalBchNeeded: bigint;
  platformFee: PlatformFeeMath;
}

export function previewClaim(ticker: string, amountUnits: bigint): ClaimTxPreview {
  const m = KNOWN_MBAS[ticker];
  if (!m) throw new Error(`unknown MBA ticker: ${ticker}`);
  const grossSats = amountUnits * m.rateSats;
  const feeSats = (grossSats * m.founderFeeBps) / 10_000n;
  const platformFee = computePlatformFee(grossSats, PLATFORM_FEE_V1);
  return {
    ticker, amountUnits, grossSats, feeSats,
    minerFee: MINER_FEE,
    totalBchNeeded: grossSats + feeSats + platformFee.emittedTotal + MINER_FEE + DUST,
    platformFee,
  };
}

// ── the primary build → sign → broadcast pipeline ──────────────────────────

export interface ClaimTxResult {
  txid: string;
  ticker: string;
  amountUnits: bigint;
  grossSats: bigint;
  feeSats: bigint;
}

export async function claimViaWc(
  wc: WcClient,
  electrum: ElectrumClient,
  args: { ticker: string; amountUnits: bigint; payerCashaddr: string; recipientTokenaddr?: string },
): Promise<ClaimTxResult> {
  const m = KNOWN_MBAS[args.ticker];
  if (!m) throw new Error(`unknown MBA ticker: ${args.ticker}`);
  const preview = previewClaim(args.ticker, args.amountUnits);

  const provider = new ElectrumNetworkProvider('chipnet');
  const contract = new Contract(
    ART,
    [txidLE(m.categoryId), m.rateSats, m.founderFeeBps, hexToBin(m.founderLockingBytecodeHex)],
    { provider },
  );

  // 1. Reservoir at the contract's tokenAddress must hold minting NFT + enough FT
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const contractUtxos = (await provider.getUtxos(contract.tokenAddress)) as any[];
  const reservoir = contractUtxos.find((u) =>
    u.token?.nft?.capability === 'minting'
    && u.token?.category === m.categoryId
    && BigInt(u.token?.amount ?? 0) >= preview.amountUnits,
  );
  if (!reservoir) throw new Error(`no reservoir UTXO for ${args.ticker} with amount >= ${preview.amountUnits}`);

  // 2. Payer BCH — via cashscript's own provider (same socket, avoids browser
  //    contention with our ./electrum.ts client used for the balance chip).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const payerAll = (await provider.getUtxos(args.payerCashaddr)) as any[];
  const payerBch = payerAll
    .filter((u) => !u.token)
    .sort((a, b) => Number(BigInt(b.satoshis) - BigInt(a.satoshis)));
  let picked: typeof payerBch = []; let total = 0n;
  for (const u of payerBch) { picked.push(u); total += BigInt(u.satoshis); if (total >= preview.totalBchNeeded) break; }
  if (total < preview.totalBchNeeded) throw new Error(`insufficient BCH: need ${preview.totalBchNeeded} sats, have ${total}`);

  // 3. Build the tx with cashscript. Use a throwaway signer for the payer's
  //    inputs — cashscript needs SOME signature bytes to serialise; the WC
  //    wallet re-signs based on sourceOutputs.lockingBytecode below.
  const throwawayKey = randomPrivateKey();
  const throwawaySig = new SignatureTemplate(throwawayKey);
  const recipient = args.recipientTokenaddr ?? args.payerCashaddr;
  const tb = new TransactionBuilder({ provider });
  tb.addInput(reservoir, contract.unlock.claim(preview.amountUnits, p2pkhLBFromCashaddr(recipient) ?? new Uint8Array(25)));
  for (const u of picked) tb.addInput(u, throwawaySig.unlockP2PKH());
  // Reservoir back to contract — FT decremented, minting NFT preserved
  const reservoirLeft = BigInt(reservoir.token.amount) - preview.amountUnits;
  tb.addOutput({ to: contract.tokenAddress, amount: BigInt(reservoir.satoshis), token: { amount: reservoirLeft, category: m.categoryId, nft: { capability: 'minting', commitment: reservoir.token.nft?.commitment ?? '' } } });
  // FT to recipient — self-backed at grossSats
  tb.addOutput({ to: recipient, amount: preview.grossSats, token: { amount: preview.amountUnits, category: m.categoryId } });
  // Founder fee (BCH) — only when > 0
  if (preview.feeSats > 0n) tb.addOutput({ to: hexToBin(m.founderLockingBytecodeHex), amount: preview.feeSats });
  // Potidaea platform fee — client-enforced (posture A). Appends two outputs
  // (platform slice + DEA-yield vault) — either may be 0 under dust waiver.
  appendPlatformFeeOutputs(tb, preview.grossSats, 'chipnet', PLATFORM_FEE_V1);
  // BCH change back to payer
  const changeSats = total - preview.grossSats - preview.feeSats - preview.platformFee.emittedTotal - MINER_FEE;
  if (changeSats < DUST) throw new Error(`change ${changeSats} below dust`);
  tb.addOutput({ to: args.payerCashaddr, amount: changeSats });

  // 4. Build (not send) — this produces a signed hex WITH the throwaway sigs
  //    on inputs 1..N. The wallet's WC signer discards them and re-signs.
  const rawTxHex = tb.build();

  // 5. Compose sourceOutputs — critical for the WC wallet to know (a) which
  //    inputs it can sign and (b) the actual pkh under each (matched via
  //    lockingBytecode against its own address set).
  const sourceOutputs: unknown[] = [];
  sourceOutputs.push({
    outpointTransactionHash: hexToBin(reservoir.txid),
    outpointIndex: reservoir.vout,
    sequenceNumber: 0xfffffffe,
    unlockingBytecode: new Uint8Array(),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    lockingBytecode: (contract as any).bytecode ? hexToBin((contract as any).bytecode) : new Uint8Array(),
    valueSatoshis: BigInt(reservoir.satoshis),
    token: {
      category: hexToBin(m.categoryId),
      amount: BigInt(reservoir.token.amount),
      nft: reservoir.token.nft
        ? { capability: reservoir.token.nft.capability, commitment: hexToBin(reservoir.token.nft.commitment ?? '') }
        : undefined,
    },
    contract: {
      abiFunction: ART.abi.find((f) => f.name === 'claim')!,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      redeemScript: (contract as any).bytecode ? hexToBin((contract as any).bytecode) : new Uint8Array(),
      artifact: ART,
    },
  });
  for (const u of picked) {
    sourceOutputs.push({
      outpointTransactionHash: hexToBin(u.txid),
      outpointIndex: u.vout,
      sequenceNumber: 0xfffffffe,
      unlockingBytecode: new Uint8Array(),
      // Real lockingBytecode of the payer's cashaddr — this is how the wallet
      // matches this input against its own key.
      lockingBytecode: p2pkhLBFromCashaddr(args.payerCashaddr) ?? new Uint8Array(25),
      valueSatoshis: BigInt(u.satoshis),
    });
  }

  // 6. Hand to WC. `broadcast: true` = the wallet broadcasts after signing;
  //    it returns the signed hex + hash for our records.
  const signedHex = await wc.signTransaction({
    transaction: rawTxHex,
    sourceOutputs,
    broadcast: true,
    userPrompt: `Claim ${preview.amountUnits} ${args.ticker} for ${preview.totalBchNeeded} sats`,
    inputPaths: picked.map((_, i) => [i + 1, 'receive', 0n] as [number, string, bigint]),
  });

  // Extract txid from signed hex (SHA256 twice, reverse) — libauth has helpers
  // for this but we compute it inline to keep the bundle lean.
  const txid = await sha256d(hexToBin(signedHex));
  return { txid, ticker: args.ticker, amountUnits: args.amountUnits, grossSats: preview.grossSats, feeSats: preview.feeSats };
}

// ── local libauth-lite helpers ─────────────────────────────────────────────

/** Convert a cashaddr → its P2PKH locking bytecode. Uses libauth's decode via
 *  cashscript's own path (Contract accepts cashaddr in outputs, so we can rely
 *  on that indirection); for the sourceOutputs shape we need the raw bytes. */
function p2pkhLBFromCashaddr(cashaddr: string): Uint8Array | null {
  try {
    const decoded = decodeCashAddress(cashaddr);
    if (typeof decoded === 'string') return null;
    return p2pkhLB(decoded.payload);
  } catch {
    return null;
  }
}

// ── local-signer variant — same tx build, but signs+broadcasts directly ────
// Used by main.ts when a local signer is loaded (dev-mode on localhost, or
// user-mode via the Attach-wallet import/create flow — see signer-store.ts).
// Bypasses WC; drives cashscript's TransactionBuilder.send() with a real
// SignatureTemplate. Real chipnet broadcast; same output shape as claimViaWc.

import type { Signer } from './signer-store';

export async function claimViaLocalSigner(
  signer: Signer,
  args: { ticker: string; amountUnits: bigint },
): Promise<ClaimTxResult> {
  const m = KNOWN_MBAS[args.ticker];
  if (!m) throw new Error(`unknown MBA ticker: ${args.ticker}`);
  const preview = previewClaim(args.ticker, args.amountUnits);
  const provider = new ElectrumNetworkProvider('chipnet');
  const contract = new Contract(
    ART,
    [txidLE(m.categoryId), m.rateSats, m.founderFeeBps, hexToBin(m.founderLockingBytecodeHex)],
    { provider },
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cutxos = (await provider.getUtxos(contract.tokenAddress)) as any[];
  const reservoir = cutxos.find((u) =>
    u.token?.nft?.capability === 'minting' && u.token?.category === m.categoryId && BigInt(u.token.amount) >= preview.amountUnits);
  if (!reservoir) throw new Error(`no reservoir for ${args.ticker}`);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const own = (await provider.getUtxos(signer.cashaddr)) as any[];
  const plain = own.filter((u) => !u.token).sort((a, b) => Number(BigInt(b.satoshis) - BigInt(a.satoshis)));
  const picked: typeof plain = []; let total = 0n;
  for (const u of plain) { picked.push(u); total += BigInt(u.satoshis); if (total >= preview.totalBchNeeded) break; }
  if (total < preview.totalBchNeeded) throw new Error(`insufficient BCH: need ${preview.totalBchNeeded}, have ${total}`);

  const sig = new SignatureTemplate(signer.privateKey);
  const recipLB = p2pkhLB(signer.publicKeyHash);
  const reservoirLeft = BigInt(reservoir.token.amount) - preview.amountUnits;
  const changeSats = total - preview.grossSats - preview.feeSats - preview.platformFee.emittedTotal - MINER_FEE;
  if (changeSats < DUST) throw new Error(`change ${changeSats} below dust`);

  const tb = new TransactionBuilder({ provider });
  tb.addInput(reservoir, contract.unlock.claim(preview.amountUnits, recipLB));
  for (const u of picked) tb.addInput(u, sig.unlockP2PKH());
  tb.addOutput({ to: contract.tokenAddress, amount: BigInt(reservoir.satoshis), token: { amount: reservoirLeft, category: m.categoryId, nft: { capability: 'minting', commitment: reservoir.token.nft?.commitment ?? '' } } });
  tb.addOutput({ to: signer.tokenaddr, amount: preview.grossSats, token: { amount: preview.amountUnits, category: m.categoryId } });
  if (preview.feeSats > 0n) tb.addOutput({ to: hexToBin(m.founderLockingBytecodeHex), amount: preview.feeSats });
  // Potidaea platform fee — client-enforced (posture A). Two extra outputs.
  appendPlatformFeeOutputs(tb, preview.grossSats, 'chipnet', PLATFORM_FEE_V1);
  tb.addOutput({ to: signer.cashaddr, amount: changeSats });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sent: any = await tb.send();
  const txid = typeof sent === 'string' ? sent : (sent.txid ?? sent.txId);
  return { txid, ticker: args.ticker, amountUnits: args.amountUnits, grossSats: preview.grossSats, feeSats: preview.feeSats };
}

// ── claim — DeviantShareMBA variant. Same reservoir/claim invariants as
// Artemision, plus a mandatory 2-way fee split enforced by the covenant:
//   outputs [2] = companySats  → companyLB (no token)
//   outputs [3] = shareholdSats → distributionLB (no token)
// Both sides must clear the 546-sat dust floor when fee is non-zero.
// Off-chain math must mirror the covenant EXACTLY (companySats = feeSats *
// companySplitBps / 10000; shareholdSats = feeSats - companySats).
//
// Not gated on WC yet — same reason as Artemision claim WC is separate:
// throwaway-signer + inputPaths for WC redeem is a follow-up. Local signer
// covers the interactive path for now.

export async function claimViaLocalSignerShare(
  signer: Signer,
  args: { ticker: string; amountUnits: bigint },
): Promise<ClaimTxResult> {
  const m = KNOWN_MBAS[args.ticker];
  if (!m) throw new Error(`unknown MBA ticker: ${args.ticker}`);
  if (m.templateId !== 0x01) throw new Error(`${args.ticker} is not a DeviantShareMBA (templateId ${m.templateId ?? 0})`);
  if (!m.distributionLockingBytecodeHex || m.companySplitBps == null) {
    throw new Error(`${args.ticker} Share MBA missing distribution / split — cannot build claim tx`);
  }

  const grossSats = args.amountUnits * m.rateSats;
  const feeSats = (grossSats * m.founderFeeBps) / 10000n;   // total fee (matches covenant)
  const companySats = (feeSats * m.companySplitBps) / 10000n;
  const shareholderSats = feeSats - companySats;
  if (feeSats > 0n && (companySats < 546n || shareholderSats < 546n)) {
    throw new Error(
      `Share MBA fee split below dust — claim ≥ ${
        // amount that produces both sides ≥ 546:
        (546n * 10000n * 10000n) / (m.rateSats * m.founderFeeBps * (m.companySplitBps > 5000n ? (10000n - m.companySplitBps) : m.companySplitBps)) + 1n
      } ${args.ticker} to satisfy the ≥546-bit dust floor on both fee outputs`,
    );
  }

  const platformFee = computePlatformFee(grossSats, PLATFORM_FEE_V1);
  const totalBchNeeded = grossSats + feeSats + platformFee.emittedTotal + MINER_FEE + DUST;
  const provider = new ElectrumNetworkProvider('chipnet');
  const companyLB = hexToBin(m.founderLockingBytecodeHex);        // Share stashes company LB in the "founder" slot
  const distributionLB = hexToBin(m.distributionLockingBytecodeHex);
  const contract = new Contract(
    SHARE_ART,
    [txidLE(m.categoryId), m.rateSats, m.founderFeeBps, m.companySplitBps, companyLB, distributionLB],
    { provider },
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cutxos = (await provider.getUtxos(contract.tokenAddress)) as any[];
  const reservoir = cutxos.find((u) =>
    u.token?.nft?.capability === 'minting' && u.token?.category === m.categoryId && BigInt(u.token.amount) >= args.amountUnits);
  if (!reservoir) throw new Error(`no reservoir for ${args.ticker}`);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const own = (await provider.getUtxos(signer.cashaddr)) as any[];
  const plain = own.filter((u) => !u.token).sort((a, b) => Number(BigInt(b.satoshis) - BigInt(a.satoshis)));
  const picked: typeof plain = []; let total = 0n;
  for (const u of plain) { picked.push(u); total += BigInt(u.satoshis); if (total >= totalBchNeeded) break; }
  if (total < totalBchNeeded) throw new Error(`insufficient BCH: need ${totalBchNeeded}, have ${total}`);

  const sig = new SignatureTemplate(signer.privateKey);
  const recipLB = p2pkhLB(signer.publicKeyHash);
  const reservoirLeft = BigInt(reservoir.token.amount) - args.amountUnits;
  const totalFeeSpent = companySats + shareholderSats;
  const changeSats = total - grossSats - totalFeeSpent - platformFee.emittedTotal - MINER_FEE;
  if (changeSats < DUST) throw new Error(`change ${changeSats} below dust`);

  const tb = new TransactionBuilder({ provider });
  tb.addInput(reservoir, contract.unlock.claim(args.amountUnits, recipLB));
  for (const u of picked) tb.addInput(u, sig.unlockP2PKH());
  tb.addOutput({
    to: contract.tokenAddress,
    amount: BigInt(reservoir.satoshis),
    token: { amount: reservoirLeft, category: m.categoryId, nft: { capability: 'minting', commitment: reservoir.token.nft?.commitment ?? '' } },
  });
  tb.addOutput({ to: signer.tokenaddr, amount: grossSats, token: { amount: args.amountUnits, category: m.categoryId } });
  if (feeSats > 0n) {
    tb.addOutput({ to: companyLB, amount: companySats });
    tb.addOutput({ to: distributionLB, amount: shareholderSats });
  }
  // Potidaea platform fee — client-enforced (posture A). Additional to the
  // Share-MBA's own founder-vs-shareholder split.
  appendPlatformFeeOutputs(tb, grossSats, 'chipnet', PLATFORM_FEE_V1);
  tb.addOutput({ to: signer.cashaddr, amount: changeSats });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sent: any = await tb.send();
  const txid = typeof sent === 'string' ? sent : (sent.txid ?? sent.txId);
  return { txid, ticker: args.ticker, amountUnits: args.amountUnits, grossSats, feeSats };
}

// ── redeem — MBA → BCH: burn FT units, release their embedded backing ──────
// DeviantOpenMintFT self-backs each FT output at `amount * backingRateSats`
// sats (covenant header comment, Self-backed FT UTXOs section). No contract
// path is required to redeem — the holder spends their FT UTXO as a normal
// P2PKH input; consensus allows FT amounts to DECREASE across a transaction
// (burn), releasing the embedded sats to plain BCH outputs.
//
// Layout (redeem `amountUnits` of a category whose reservoir is at rate R):
//   inputs:  N of the user's FT UTXOs for the category, totalling ≥ amountUnits
//            + optionally plain BCH inputs for extra fee headroom
//   output0: if leftover > 0 — send leftover FT back to user, self-backed
//            at leftover*R sats (preserves the covenant invariant so the
//            leftover UTXO can round-trip through claim later)
//   output1: plain BCH to user — the released backing minus miner fee
//
// No founder fee — the fee lives on the claim() path only; redeem is a
// user-only spend.

export interface RedeemTxResult {
  txid: string;
  ticker: string;
  amountUnits: bigint;
  releasedSats: bigint;
}

export async function redeemViaLocalSigner(
  signer: import('./signer-store').Signer,
  args: { ticker: string; amountUnits: bigint },
): Promise<RedeemTxResult> {
  const m = KNOWN_MBAS[args.ticker];
  if (!m) throw new Error(`unknown MBA ticker: ${args.ticker}`);
  if (args.amountUnits < 1n) throw new Error('amount too small');

  const provider = new ElectrumNetworkProvider('chipnet');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const own = (await provider.getUtxos(signer.cashaddr)) as any[];
  const tokens = own.filter((u) => u.token?.category === m.categoryId && !u.token?.nft);
  tokens.sort((a, b) => Number(BigInt(b.token.amount) - BigInt(a.token.amount)));

  const picked: typeof tokens = []; let haveUnits = 0n; let haveSats = 0n;
  for (const u of tokens) {
    picked.push(u);
    haveUnits += BigInt(u.token.amount);
    haveSats += BigInt(u.satoshis);
    if (haveUnits >= args.amountUnits) break;
  }
  if (haveUnits < args.amountUnits) throw new Error(`insufficient ${args.ticker}: need ${args.amountUnits}, have ${haveUnits}`);

  const leftoverUnits = haveUnits - args.amountUnits;
  const leftoverBacking = leftoverUnits * m.rateSats;
  const redeemedBacking = args.amountUnits * m.rateSats;

  // Fee headroom — if the released sats aren't enough for miner fee + dust,
  // add plain BCH inputs.
  const plain = own.filter((u) => !u.token).sort((a, b) => Number(BigInt(b.satoshis) - BigInt(a.satoshis)));
  let extraSats = 0n; const extraPicked: typeof plain = [];
  const targetSats = redeemedBacking - MINER_FEE;
  if (targetSats < DUST) {
    // Need extra BCH to fund miner fee and still leave a valid output
    for (const u of plain) {
      extraPicked.push(u); extraSats += BigInt(u.satoshis);
      if (redeemedBacking + extraSats - MINER_FEE >= DUST) break;
    }
    if (redeemedBacking + extraSats - MINER_FEE < DUST) {
      throw new Error(`redeem too small to cover miner fee (need extra plain BCH)`);
    }
  }

  const outputSats = redeemedBacking + extraSats - MINER_FEE;
  const sig = new SignatureTemplate(signer.privateKey);

  // allowImplicitFungibleTokenBurn: cashscript's safety check flags any tx
  // where FT input > FT output as a mistake. Burn-for-backing-release is
  // exactly that shape by design; opt in.
  const tb = new TransactionBuilder({ provider, allowImplicitFungibleTokenBurn: true });
  for (const u of picked) tb.addInput(u, sig.unlockP2PKH());
  for (const u of extraPicked) tb.addInput(u, sig.unlockP2PKH());

  if (leftoverUnits > 0n) {
    tb.addOutput({
      to: signer.tokenaddr,
      amount: leftoverBacking >= DUST ? leftoverBacking : DUST,
      token: { amount: leftoverUnits, category: m.categoryId },
    });
    // If leftover backing was below dust and we padded up, that padding has
    // to come from the released BCH — reduce the plain output accordingly.
    if (leftoverBacking < DUST) {
      const pad = DUST - leftoverBacking;
      if (outputSats - pad < DUST) throw new Error('leftover dust padding leaves plain output below dust');
    }
  }
  tb.addOutput({ to: signer.cashaddr, amount: outputSats - (leftoverUnits > 0n && leftoverBacking < DUST ? (DUST - leftoverBacking) : 0n) });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sent: any = await tb.send();
  const txid = typeof sent === 'string' ? sent : (sent.txid ?? sent.txId);
  return { txid, ticker: args.ticker, amountUnits: args.amountUnits, releasedSats: redeemedBacking };
}

async function sha256d(bytes: Uint8Array): Promise<string> {
  const a = await crypto.subtle.digest('SHA-256', bytes);
  const b = await crypto.subtle.digest('SHA-256', a);
  const arr = new Uint8Array(b);
  // display txid = big-endian reverse
  const rev = new Uint8Array(32);
  for (let i = 0; i < 32; i++) rev[i] = arr[31 - i];
  let s = ''; for (const x of rev) s += x.toString(16).padStart(2, '0'); return s;
}
